"use client";

/**
 * Enterprise context provider for multi-tenant enterprise configuration
 * Provides enterprise slug, logo, theme colors, and organization info to client components
 * Data is populated server-side in layout.tsx and passed down via context
 */

import { createContext, useContext, type ReactNode } from "react";
import type { IndigoColorPalette } from "@/utils/enterprise-theme-types";

/** Enterprise logo configuration */
export type EnterpriseLogo = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

/** Enterprise theme colors for consistent server/client rendering */
export type EnterpriseTheme = {
  primaryColor: string;
  primaryHoverColor: string;
  primarySecondaryBg: string;
  primarySecondaryHoverBg: string;
  indigoColors: IndigoColorPalette | null;
};

/** Enterprise context value */
export type EnterpriseContextValue = {
  /** Enterprise slug from subdomain (e.g., "blackrock" from blackrock.nowadays.ai) */
  slug: string | null;
  /** Enterprise logo configuration */
  logo: EnterpriseLogo | null;
  /** Organization name */
  orgName: string | null;
  /** Enterprise theme colors (null = use defaults) */
  theme: EnterpriseTheme | null;
};

/** Default theme colors used when no enterprise theme is set */
const DEFAULT_THEME: EnterpriseTheme = {
  primaryColor: "#5261EB",
  primaryHoverColor: "#4652D1",
  primarySecondaryBg: "#e0e4ff",
  primarySecondaryHoverBg: "#d1d9ff",
  indigoColors: null,
};

const defaultValue: EnterpriseContextValue = {
  slug: null,
  logo: null,
  orgName: null,
  theme: null,
};

const EnterpriseContext = createContext<EnterpriseContextValue>(defaultValue);

/**
 * Hook to access enterprise context
 * Returns slug, logo, orgName, and theme for the current enterprise tenant
 *
 * @example
 * const { slug, logo, orgName } = useEnterprise();
 * if (logo) {
 *   return <img src={logo.src} alt={logo.alt} />;
 * }
 */
export function useEnterprise(): EnterpriseContextValue {
  return useContext(EnterpriseContext);
}

/**
 * Hook to access enterprise theme colors with fallbacks
 * Always returns valid theme colors (enterprise or defaults)
 *
 * @example
 * const { primaryColor, primarySecondaryBg } = useEnterpriseTheme();
 */
export function useEnterpriseTheme(): EnterpriseTheme {
  const { theme } = useEnterprise();
  return theme ?? DEFAULT_THEME;
}

interface EnterpriseProviderProps {
  children: ReactNode;
  value: EnterpriseContextValue;
}

/**
 * Provider component for enterprise context
 * Wrap your app with this in layout.tsx and pass enterprise data from server
 */
export function EnterpriseProvider({ children, value }: EnterpriseProviderProps) {
  return <EnterpriseContext.Provider value={value}>{children}</EnterpriseContext.Provider>;
}
